import PublicLayout from "@/components/layout/PublicLayout";
import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { claimUpload } from "@/lib/api";
import { toast } from "sonner";
import AppleSignInButton from "@/components/auth/AppleSignInButton";
import { getLastAuthMethod } from "@/lib/lastAuthMethod";

function GoogleIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path
                fill="#4285F4"
                d="M17.64 9.205c0-.638-.057-1.252-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
            />
            <path
                fill="#34A853"
                d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.836.86-3.048.86-2.344 0-4.328-1.583-5.036-3.71H.957v2.332A8.997 8.997 0 009 18z"
            />
            <path
                fill="#FBBC05"
                d="M3.964 10.71A5.41 5.41 0 013.68 9c0-.593.102-1.17.284-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
            />
            <path
                fill="#EA4335"
                d="M9 3.58c1.32 0 2.506.454 3.44 1.345l2.58-2.58C13.463.893 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
            />
        </svg>
    );
}

export default function AuthPage({ mode = "login", gate = false }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [optIn, setOptIn] = useState(false);
    const [trustDevice, setTrustDevice] = useState(false);
    const [loading, setLoading] = useState(false);
    const [params] = useSearchParams();
    const next = params.get("next") || "/dashboard";
    const upload_id = params.get("upload_id");
    const ref = params.get("ref"); // share token for referral attribution
    const navigate = useNavigate();
    const { user, loginWithEmail, registerWithEmail, signInWithGoogle } = useAuth();

    // Smart auth-method memory — surface the last-used method on this device
    // as the primary big button, tuck the others behind "Use a different
    // method". Only applies to the login mode (register UX should show all
    // options equally since a new user has no "last used"). The user can
    // expand the other options at any time.
    const lastMethod = mode === "login" ? getLastAuthMethod() : null;
    const [showAllMethods, setShowAllMethods] = useState(!lastMethod);

    // If the user needs onboarding (just signed up, never completed it,
    // never skipped) send them through /onboarding first. We honour an
    // explicit `?next=` so deep-link returns (e.g. claim-upload flow)
    // still work — onboarding deliberately doesn't gate report claiming.
    const computeNext = (u) => {
        if (!u) return next;
        const needsOnboarding =
            !u.onboarding_completed
            && !u.onboarding_skipped
            && next === "/dashboard"; // only intercept the default landing
        return needsOnboarding ? "/onboarding" : next;
    };

    useEffect(() => {
        if (user) {
            // already authed — claim if needed then continue
            (async () => {
                if (upload_id) {
                    try {
                        await claimUpload(upload_id);
                    } catch { /* non-critical */ }
                }
                navigate(computeNext(user));
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, navigate, next, upload_id]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            let authedUser = null;
            if (mode === "register") {
                const referral_from = ref || (typeof window !== "undefined" ? localStorage.getItem("bl_referral_share_token") : null);
                authedUser = await registerWithEmail({
                    email,
                    password,
                    name,
                    email_opt_in: optIn,
                    referral_from: referral_from || undefined,
                });
                // PostHog: backend auto-fires the verification email on register.
                try {
                    track("auth_register_success", { email_opt_in: optIn, has_referral: !!referral_from });
                    track("email_verification_sent", { email });
                } catch { /* non-critical */ }
                try {
                    localStorage.removeItem("bl_referral_share_token");
                } catch { /* non-critical */ }
            } else {
                authedUser = await loginWithEmail(email, password, { trustDevice });
                try {
                    track("auth_login_success");
                } catch { /* non-critical */ }
            }
            if (upload_id) {
                try {
                    await claimUpload(upload_id);
                } catch { /* non-critical */ }
            }
            navigate(computeNext(authedUser));
        } catch (err) {
            try {
                track("auth_error", { mode, message: (err?.response?.data?.detail || "unknown").slice(0, 80) });
            } catch { /* non-critical */ }
            toast.error(
                err?.response?.data?.detail ||
                    (mode === "register"
                        ? "Could not create account."
                        : "Invalid email or password."),
            );
        } finally {
            setLoading(false);
        }
    };

    const onGoogle = () => {
        // redirect URL must be derived from window.location.origin
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const cb = "/auth/callback?next=" + encodeURIComponent(next) + (upload_id ? "&upload_id=" + upload_id : "");
        signInWithGoogle(cb);
    };

    return (
        <PublicLayout>
            <section className="bl-paper">
                <div className="bl-container py-16 max-w-md" data-testid="auth-page">
                    <div className="bl-eyebrow">
                        {gate ? "Almost there" : mode === "register" ? "Create account" : "Welcome back"}
                    </div>
                    <h1 className="mt-4 font-display font-semibold text-3xl sm:text-4xl md:text-5xl tracking-tight text-bl leading-tight">
                        {gate ? (
                            <>
                                Your preview is{" "}
                                <span className="font-italic-accent text-bl-primary">
                                    almost ready.
                                </span>
                            </>
                        ) : mode === "register" ? (
                            "Create your free Buyer's Leverage account."
                        ) : (
                            "Sign in to Buyer's Leverage."
                        )}
                    </h1>
                    <p className="mt-4 text-bl-muted leading-relaxed">
                        {gate
                            ? "Create your free Buyer's Leverage account to securely access your inspection preview and saved reports."
                            : mode === "register"
                              ? "Save your reports, track entitlements, and pick up where you left off."
                              : "Continue to your dashboard and saved reports."}
                    </p>

                    <div className="mt-8 bl-card p-6 md:p-7">
                        {lastMethod && !showAllMethods && (
                            <div
                                className="mb-3 text-[11px] tracking-[0.15em] uppercase text-bl-muted"
                                data-testid="auth-welcome-back-hint"
                            >
                                Welcome back · last signed in with{" "}
                                <span className="text-bl font-semibold">
                                    {lastMethod === "google" && "Google"}
                                    {lastMethod === "apple" && "Apple"}
                                    {lastMethod === "email" && "email"}
                                </span>
                            </div>
                        )}

                        {/* Primary method (last-used) or all-options layout */}
                        {(!lastMethod || showAllMethods || lastMethod === "google") && (
                            <button
                                onClick={onGoogle}
                                className={
                                    "w-full flex items-center justify-center gap-3 px-5 py-3.5 rounded-full border border-bl bg-bl-surface hover:border-bl-primary transition-colors text-bl font-medium"
                                    + (lastMethod === "google" && !showAllMethods
                                        ? " ring-2 ring-bl-primary"
                                        : "")
                                }
                                data-testid="google-signin-btn"
                                type="button"
                            >
                                <GoogleIcon /> Continue with Google
                            </button>
                        )}
                        {(!lastMethod || showAllMethods || lastMethod === "apple") && (
                            <div className={lastMethod === "apple" && !showAllMethods ? "" : "mt-3"}>
                                <AppleSignInButton
                                    persona={params.get("persona") || ""}
                                    emailOptIn={optIn}
                                    next={next}
                                />
                            </div>
                        )}

                        {/* "Use a different method" toggle — only when collapsed */}
                        {lastMethod && !showAllMethods && (
                            <button
                                type="button"
                                onClick={() => setShowAllMethods(true)}
                                data-testid="auth-show-all-methods-btn"
                                className="mt-3 w-full text-xs text-bl-muted hover:text-bl underline-offset-2 hover:underline transition-colors"
                            >
                                Use a different sign-in method
                            </button>
                        )}

                        {/* "or" divider hidden when only the email form is below
                            (lastMethod=email and we collapsed the social
                            buttons — no need to separate from nothing) */}
                        {(!lastMethod || showAllMethods || lastMethod !== "email") && (
                            <div className="flex items-center gap-3 my-5">
                                <div className="flex-1 h-px bg-bl-border" />
                                <span className="text-xs text-bl-muted">or</span>
                                <div className="flex-1 h-px bg-bl-border" />
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            {mode === "register" && (
                                <div>
                                    <label className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                        Name
                                    </label>
                                    <input
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="mt-2 w-full px-4 py-3 bg-bl-bg rounded-lg border border-bl text-bl outline-none focus:border-bl-primary"
                                        data-testid="auth-name-input"
                                    />
                                </div>
                            )}
                            <div>
                                <label className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                    Email
                                </label>
                                <input
                                    required
                                    type="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="mt-2 w-full px-4 py-3 bg-bl-bg rounded-lg border border-bl text-bl outline-none focus:border-bl-primary"
                                    data-testid="auth-email-input"
                                />
                            </div>
                            <div>
                                <label className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                    Password
                                </label>
                                <input
                                    required
                                    type="password"
                                    autoComplete={
                                        mode === "register"
                                            ? "new-password"
                                            : "current-password"
                                    }
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="mt-2 w-full px-4 py-3 bg-bl-bg rounded-lg border border-bl text-bl outline-none focus:border-bl-primary"
                                    minLength={8}
                                    data-testid="auth-password-input"
                                />
                            </div>

                            {mode === "register" && (
                                <label className="flex items-start gap-3 text-sm text-bl-muted">
                                    <input
                                        type="checkbox"
                                        checked={optIn}
                                        onChange={(e) => setOptIn(e.target.checked)}
                                        data-testid="auth-optin-checkbox"
                                        className="mt-1"
                                    />
                                    <span>
                                        Get strategic buyer insights, inspection tips, and
                                        homeowner guidance delivered occasionally.
                                    </span>
                                </label>
                            )}

                            {mode === "login" && (
                                <label
                                    className="flex items-start gap-3 text-sm text-bl-muted"
                                    data-testid="auth-trust-device-label"
                                >
                                    <input
                                        type="checkbox"
                                        checked={trustDevice}
                                        onChange={(e) => setTrustDevice(e.target.checked)}
                                        data-testid="auth-trust-device-checkbox"
                                        className="mt-1"
                                    />
                                    <span>
                                        <span className="text-bl">Trust this device for 30 days.</span>{" "}
                                        Skips the &ldquo;new login&rdquo; security email when you sign in
                                        from this browser. Don&apos;t check on shared computers.
                                    </span>
                                </label>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className="bl-btn-primary w-full"
                                data-testid="auth-submit-btn"
                            >
                                {loading
                                    ? "Working…"
                                    : mode === "register"
                                      ? "Create account"
                                      : "Sign in"}
                            </button>

                            {mode === "login" && (
                                <div className="flex flex-col gap-1 pt-2 text-xs text-bl-muted text-center">
                                    <Link
                                        to="/forgot-password"
                                        className="text-bl-primary hover:underline"
                                        data-testid="auth-forgot-password-link"
                                    >
                                        Forgot password?
                                    </Link>
                                    <Link
                                        to="/recover-account"
                                        className="hover:text-bl-primary hover:underline"
                                        data-testid="auth-forgot-email-link"
                                    >
                                        Forgot which email you used?
                                    </Link>
                                </div>
                            )}
                        </form>
                    </div>

                    <p className="mt-6 text-center text-bl-muted text-sm">
                        {mode === "register" ? (
                            <>
                                Already have an account?{" "}
                                <Link
                                    to="/auth/login"
                                    className="text-bl-primary hover:underline"
                                    data-testid="auth-switch-login-link"
                                >
                                    Sign in
                                </Link>
                            </>
                        ) : (
                            <>
                                New here?{" "}
                                <Link
                                    to="/auth/register"
                                    className="text-bl-primary hover:underline"
                                    data-testid="auth-switch-register-link"
                                >
                                    Create account
                                </Link>
                            </>
                        )}
                    </p>
                </div>
            </section>
        </PublicLayout>
    );
}
